import { useState } from 'react'
import { fbt } from 'fbtee'
import { ExternalLink } from 'lucide-react'
import { Skeleton } from './ui/skeleton'
import { cn } from '../lib/utils'
import type { ArtistDetails } from '../types'

// Past this, a full-page bio pushes the events off screen; it starts clamped.
const LONG_BIO_CHARS = 320

const SOCIAL_LABELS: Record<keyof NonNullable<ArtistDetails['socialLinks']>, string> = {
  instagram: 'Instagram',
  soundcloud: 'SoundCloud',
  bandcamp: 'Bandcamp',
  website: 'Website',
}

/** An artist's bio + social link chips. `compact` is the smaller type used inside the set dialog. */
export function ArtistBio({
  details, loading, compact,
}: { details: ArtistDetails | null; loading: boolean; compact?: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const clampable = !compact && (details?.bio?.length ?? 0) > LONG_BIO_CHARS
  const socials = details?.socialLinks ?? {}
  const socialEntries = (Object.keys(SOCIAL_LABELS) as (keyof typeof SOCIAL_LABELS)[])
    .map(key => ({ key, url: socials[key] }))
    .filter((entry): entry is { key: keyof typeof SOCIAL_LABELS; url: string } => Boolean(entry.url))

  return (
    <div className="space-y-3">
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-[85%]" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-[78%]" />
        </div>
      ) : details?.shortBio || details?.bio ? (
        <div className="space-y-3">
          {details.shortBio && (
            <p className={cn('leading-snug whitespace-pre-line', compact ? 'text-sm' : 'text-base')}>
              {details.shortBio}
            </p>
          )}
          {details.bio && (
            <div className="space-y-1">
              <p
                className={cn(
                  'leading-relaxed text-muted-foreground whitespace-pre-line',
                  compact ? 'text-xs' : 'text-sm',
                  clampable && !expanded && 'line-clamp-5',
                )}
              >
                {details.bio}
              </p>
              {clampable && (
                <button
                  type="button"
                  onClick={() => setExpanded(e => !e)}
                  className="cursor-pointer text-sm font-medium text-primary"
                >
                  {expanded
                    ? fbt('Show less', 'Button that collapses a long artist bio back to a few lines')
                    : fbt('Show more', 'Button that expands a truncated artist bio to its full length')}
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {fbt(
            'No description yet.',
            'Empty state shown in the artist details dialog when no description has been written yet',
          )}
        </p>
      )}
      {socialEntries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {socialEntries.map(({ key, url }) => (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs hover:border-primary/50 hover:text-primary transition-colors"
            >
              {SOCIAL_LABELS[key]}
              <ExternalLink className="size-3" aria-hidden />
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
